import type { Tournament } from '@swiss-magic/shared-types';
import { getPublicTournament } from './api';
import { getProfile } from './api/profile';
import { enroll } from './api/enrollment';
import { followTournament } from './api/follow';
import { subscribeToDirector } from './api/subscriptions';
import { ApiError } from './api/core';
import { showErrorToast } from './toast';
import { hasCreatedProfile } from './profileStatus';

type Navigate = (path: string) => void;

/**
 * Generic intent consumer — used by all three "do X after auth, then
 * land on the tournament page" flows. The action takes the resolved
 * Tournament so consumers that need its `organizerId` (subscribe) or
 * `id` (enroll, follow) get them without a second fetch.
 *
 * Errors get toasted; the navigate-to-/t-slug always runs so the user
 * never lands on the auth page after a stale intent.
 */
async function consumeIntentAfterAuth(
  slug: string,
  navigate: Navigate,
  action: (tournament: Tournament) => Promise<void>,
  fallbackErrorMessage: string,
): Promise<void> {
  try {
    const tournament = await getPublicTournament(slug);
    await action(tournament);
  } catch (err) {
    if (err instanceof ApiError) showErrorToast(err.userMessage);
    else showErrorToast(fallbackErrorMessage);
  }
  navigate(`/t/${slug}`);
}

/**
 * Consume a `registerFor=<slug>` intent after the user has just authenticated.
 * We don't yet know whether the user has a profile, so branch accordingly.
 */
export async function consumeRegisterForAfterAuth(
  slug: string,
  navigate: Navigate,
): Promise<void> {
  try {
    const profile = await getProfile();
    if (!hasCreatedProfile(profile)) {
      navigate(`/profile?registerFor=${encodeURIComponent(slug)}`);
      return;
    }
    await consumeRegisterForAfterProfileSave(slug, navigate);
  } catch (err) {
    if (err instanceof ApiError) showErrorToast(err.userMessage);
    navigate(`/t/${slug}`);
  }
}

/**
 * Consume the intent after a profile save — profile existence is implied.
 */
export async function consumeRegisterForAfterProfileSave(
  slug: string,
  navigate: Navigate,
): Promise<void> {
  await consumeIntentAfterAuth(
    slug,
    navigate,
    (t) => enroll(t.id).then(() => undefined),
    'Could not complete registration',
  );
}

/**
 * Story 016 — consume a `followFor=<slug>` intent after the user has just
 * authenticated. Following has no profile gate, so the flow is simpler
 * than the registerFor counterpart: hit /follow and bounce back to the
 * tournament page in Following state.
 */
export async function consumeFollowForAfterAuth(
  slug: string,
  navigate: Navigate,
): Promise<void> {
  await consumeIntentAfterAuth(
    slug,
    navigate,
    (t) => followTournament(t.id).then(() => undefined),
    'Could not follow this tournament',
  );
}

/**
 * Story 015 — consume a `subscribeForOrganizerOf=<slug>` intent after
 * auth. Resolve the organizer from the tournament, subscribe, and
 * bounce back to the tournament page where the user came from. No
 * profile gate (subscriptions are spectator affordances).
 */
export async function consumeSubscribeForOrganizerOfAfterAuth(
  slug: string,
  navigate: Navigate,
): Promise<void> {
  await consumeIntentAfterAuth(
    slug,
    navigate,
    (t) => subscribeToDirector(t.organizerId).then(() => undefined),
    'Could not subscribe to this director',
  );
}
